/**
 * CartDrawer - Slide-out cart panel
 */
import { Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet'
import { Separator } from '../ui/separator'
import { useCartStore } from '../../stores/cart'
import type { CartItem } from '../../stores/cart'

function EmptyCart({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
      <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="font-medium mb-1">Your cart is empty</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Browse products and add items to get started
      </p>
      <Button variant="outline" onClick={onClose} asChild>
        <Link to="/products">Browse Products</Link>
      </Button>
    </div>
  )
}

function CartItemRow({ item, onUpdateQuantity, onRemove }: {
  item: CartItem
  onUpdateQuantity: (offerId: string, qty: number) => void
  onRemove: (offerId: string) => void
}) {
  return (
    <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm line-clamp-2">{item.name}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {item.vendorName} · {item.distributorCode}
        </p>
        {item.billingCycle && (
          <Badge variant="outline" className="text-xs mt-1">{item.billingCycle}</Badge>
        )}
        <div className="flex items-center gap-2 mt-2">
          <Button
            variant="outline" size="icon" className="h-7 w-7"
            onClick={() => onUpdateQuantity(item.offerId, item.quantity - 1)}
            disabled={item.quantity <= (item.minQuantity || 1)}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
          <Button
            variant="outline" size="icon" className="h-7 w-7"
            onClick={() => onUpdateQuantity(item.offerId, item.quantity + 1)}
            disabled={item.maxQuantity !== null && item.quantity >= item.maxQuantity}
          >
            <Plus className="h-3 w-3" />
          </Button>
          <span className="text-xs text-muted-foreground">licenses</span>
        </div>
      </div>
      <Button
        variant="ghost" size="icon"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={() => onRemove(item.offerId)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}

function CartContents({ items, totalItems, onUpdateQuantity, onRemove, onClear, onClose }: {
  items: CartItem[]; totalItems: number
  onUpdateQuantity: (offerId: string, qty: number) => void
  onRemove: (offerId: string) => void
  onClear: () => void; onClose: (open: boolean) => void
}) {
  return (
    <>
      <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4">
        {items.map((item) => (
          <CartItemRow key={item.offerId} item={item} onUpdateQuantity={onUpdateQuantity} onRemove={onRemove} />
        ))}
      </div>
      <Separator className="my-4" />
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Total licenses</span>
          <span className="font-medium">{totalItems}</span>
        </div>
        <Button className="w-full" asChild onClick={() => onClose(false)}>
          <Link to="/checkout">Proceed to Checkout</Link>
        </Button>
        <Button variant="ghost" size="sm" className="w-full" onClick={onClear}>
          <Trash2 className="h-3 w-3 mr-2" />
          Clear Cart
        </Button>
      </div>
    </>
  )
}

export function CartDrawer() {
  const { items, isOpen, setCartOpen, removeItem, updateQuantity, clearCart } = useCartStore()
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0)

  return (
    <Sheet open={isOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Cart
            {totalItems > 0 && (
              <Badge variant="secondary" className="ml-1">{totalItems}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <EmptyCart onClose={() => setCartOpen(false)} />
        ) : (
          <CartContents
            items={items} totalItems={totalItems}
            onUpdateQuantity={updateQuantity} onRemove={removeItem}
            onClear={clearCart} onClose={setCartOpen}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}

/** Floating cart button for the header */
export function CartButton() {
  const { items, toggleCart } = useCartStore()
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0)

  return (
    <Button variant="ghost" size="icon" className="relative h-10 w-10" onClick={toggleCart}>
      <ShoppingCart className="h-5 w-5" />
      {totalItems > 0 && (
        <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center">
          {totalItems}
        </span>
      )}
    </Button>
  )
}
